/* Honest plane-status provenance (pipeline-productionization H1 + H2).
 *
 * PURE module — no DOM, no globals, first-parameter state, mirroring
 * derive/scope.ts. Two responsibilities, one plane grammar:
 *
 *   H1 — post-run consumption of `result.enrichment.planeStatus` (stamped by
 *        service/tools/enrich.py). Turns each plane's stamped status into the
 *        ONE honest one-line provenance copy, so a `skipped` (deliberate
 *        scope) vs `unavailable`/`error` (dark) plane can never read as
 *        "missing / claimable / coming-soon" — the 3rd repeat of the
 *        stamped-but-never-rendered failure (after parity self-diff and the
 *        profiles `skippedLayers` line derive/scope.ts fixed).
 *
 *   H2 — pre-run consumption of `GET /system/planes`. Turns the per-plane
 *        availability snapshot into run-modal toggle rows (default ON for an
 *        available plane, default OFF-with-amber-reason for a dark one — the
 *        signed D8 ruling), plus the `include` array and the conditional
 *        modal lede.
 *
 * The pill grammar is shared and load-bearing (grammar rider, signed):
 *   ok / reused        → neutral / meta   (the plane ran — never a warning)
 *   skipped            → neutral pill-meta (deliberate scope — never a warning)
 *   unavailable / error→ amber pill-state.warn (our debt — NEVER red; a dark
 *                        plane is never client adversity, same logic as the
 *                        pill-spec red budget). `error` differs from
 *                        `unavailable` in COPY, not colour (signed D9), and an
 *                        `error` on a section that still rendered content shows
 *                        the amber line WITHOUT suppressing that content.
 */

/** The four selectable cross-reference planes (mirrors enrich.PLANES). */
export const PLANES = ['linkgraph', 'impact', 'aiq', 'peers'] as const;
export type PlaneKey = (typeof PLANES)[number];

/** The five states service/tools/enrich.py stamps per plane. */
export type PlaneStatusValue = 'ok' | 'reused' | 'skipped' | 'unavailable' | 'error';

/** One plane's stamped run status (result.enrichment.planeStatus[plane]). */
export interface PlaneStatusEntry {
  requested?: boolean;
  ran?: boolean;
  status?: string;
  reason?: string | null;
  asOf?: string | null;
  ageDays?: number | null;
  stale?: boolean;
  [k: string]: unknown;
}

export type PlaneStatusMap = Record<string, PlaneStatusEntry>;

/** The three-way pill grammar bucket (grammar rider). */
export type PlaneTone = 'ok' | 'meta' | 'warn';

/** ok/reused → ran (neutral/positive) · skipped → neutral metadata ·
 * unavailable/error → amber. A dark plane is NEVER 'bad'/red. */
export function planeTone(status: string | null | undefined): PlaneTone {
  switch (status) {
    case 'ok':
    case 'reused':
      return 'ok';
    case 'unavailable':
    case 'error':
      return 'warn';
    default: // 'skipped' and any unknown value → neutral metadata
      return 'meta';
  }
}

/** True for a dark plane (credential/cache dark OR requested-but-threw). Both
 * render amber — never red. */
export function isDarkPlane(status: string | null | undefined): boolean {
  return status === 'unavailable' || status === 'error';
}

/** Map a plane tone to the approved pill archetype class (grammar rider):
 * warn → amber `pill-state warn`; ok/meta → neutral `pill-meta`. NEVER `bad`
 * (a dark plane is never red). */
export function planePillClass(tone: PlaneTone): string {
  return tone === 'warn' ? 'pill-state warn' : 'pill-meta';
}

/** Long provenance label for a plane (coverage appendix + generic copy). */
const PLANE_LABEL: Record<string, string> = {
  linkgraph: 'Link-graph corroboration',
  impact: 'IMPACT search polling',
  aiq: 'AIQ narrative',
  peers: 'Peer positioning',
};

export function planeLabel(plane: string): string {
  return PLANE_LABEL[plane] || plane;
}

/** Safe accessor for one plane's stamped status off a run's planeStatus map.
 * Returns null for a legacy run (no planeStatus) so callers render their own
 * legacy neutral copy rather than a fabricated status. */
export function planeState(
  planeStatus: PlaneStatusMap | null | undefined,
  plane: string,
): PlaneStatusEntry | null {
  if (!planeStatus || typeof planeStatus !== 'object') return null;
  const e = planeStatus[plane];
  return e && typeof e === 'object' ? e : null;
}

export interface PlaneProvenance {
  tone: PlaneTone;
  text: string;
}

/** The honest one-line provenance copy for a plane's stamped status — the
 * single grammar every empty-state fallback and the coverage appendix share.
 * Returns null when there is no stamped status (a legacy run); callers then
 * render their own legacy neutral copy. The `error` copy is deliberately
 * DISTINCT from `unavailable` (signed D9) yet the same amber tone. */
export function planeProvenanceLine(
  plane: string,
  entry: PlaneStatusEntry | null | undefined,
): PlaneProvenance | null {
  if (!entry || !entry.status) return null;
  const status = entry.status;
  const tone = planeTone(status);
  const reason = (entry.reason == null ? '' : String(entry.reason)).trim();
  const label = planeLabel(plane);

  if (status === 'skipped') {
    // Neutral metadata — deliberate scope, NEVER a warning.
    if (plane === 'impact') return { tone, text: 'IMPACT search polling not requested for this run' };
    if (plane === 'aiq') return { tone, text: 'AIQ narrative not requested for this run' };
    return { tone, text: `${label} not requested for this run` };
  }

  if (status === 'unavailable') {
    // Amber — dark plane (credential/cache absent). Reason inlined.
    if (plane === 'impact') return { tone, text: `IMPACT not polled: ${reason || 'no session cookie'}` };
    if (plane === 'aiq')
      return { tone, text: `AIQ narrative unavailable: ${reason || 'no cached poll / credential'}` };
    return { tone, text: `${label} unavailable${reason ? `: ${reason}` : ''}` };
  }

  if (status === 'error') {
    // Amber — our debt (requested + attempted, threw). Distinct copy per D9;
    // "data may be partial" signals content may still be present (never red,
    // never suppressed).
    const why = reason || 'unknown error';
    if (plane === 'impact') return { tone, text: `IMPACT poll failed: ${why} — data may be partial` };
    if (plane === 'aiq') return { tone, text: `AIQ narrative poll failed: ${why} — data may be partial` };
    return { tone, text: `${label} failed: ${why} — data may be partial` };
  }

  if (status === 'reused') {
    // Staleness meta line (grammar rider), never a warning.
    const age = entry.ageDays;
    return { tone, text: `${label} reused${typeof age === 'number' ? ` · cache ${age}d old` : ''}` };
  }

  // ok
  if (plane === 'impact') return { tone, text: 'IMPACT search polling joined this run' };
  if (plane === 'aiq') return { tone, text: 'AIQ narrative joined this run' };
  return { tone, text: `${label} ran this run` };
}

/* ─────────────────────────── H2 — pre-run toggles ───────────────────────── */

/** One plane's entry in `GET /system/planes` → `{planes: {...}}`. */
export interface SystemPlaneEntry {
  status?: string; // 'available' | 'unavailable'
  reason?: string | null;
  cache?: {
    brands?: number;
    stale?: number;
    entries?: Array<{ slug?: string; asOf?: string | null; ageDays?: number | null; stale?: boolean }>;
  };
  [k: string]: unknown;
}

export type SystemPlanesMap = Record<string, SystemPlaneEntry>;

/** A run-modal plane toggle row. */
export interface PlaneToggle {
  key: PlaneKey;
  label: string;
  available: boolean;
  /** Signed D8: available planes default ON (opt-out); a dark plane stays
   * VISIBLE + INTERACTIVE but defaults OFF. */
  defaultOn: boolean;
  /** Inline note: an amber reason for a dark plane, an amber age note for a
   * stale-but-present AIQ cache, or a neutral cache-present meta. */
  note: string | null;
  tone: PlaneTone;
}

/** Short toggle labels (modal chrome). */
const TOGGLE_LABEL: Record<string, string> = {
  linkgraph: 'Link graph',
  impact: 'IMPACT search',
  aiq: 'AI narrative (AIQ)',
  peers: 'Peer set',
};

function aiqCacheNote(
  entry: SystemPlaneEntry,
  brandSlug?: string | null,
): { text: string; stale: boolean } | null {
  const cache = entry.cache || {};
  const entries = cache.entries || [];
  if (brandSlug) {
    const m = entries.find((e) => e.slug === brandSlug);
    if (m && typeof m.ageDays === 'number') {
      return { text: `cache ${m.ageDays}d old${m.stale ? ' (stale)' : ''}`, stale: !!m.stale };
    }
  }
  const stale = cache.stale || 0;
  const brands = cache.brands ?? entries.length;
  if (stale > 0) return { text: `cache stale for ${stale} of ${brands} brand${brands === 1 ? '' : 's'}`, stale: true };
  if (brands > 0) return { text: `cache present · ${brands} brand${brands === 1 ? '' : 's'}`, stale: false };
  return null;
}

/** Build the run-modal toggle model from `GET /system/planes`. `brandSlug`
 * (when the AM has typed a client) picks that brand's AIQ cache-age note. */
export function planeToggleModel(
  planes: SystemPlanesMap | null | undefined,
  brandSlug?: string | null,
): PlaneToggle[] {
  const p = planes || {};
  return PLANES.map((key): PlaneToggle => {
    const entry = p[key] || {};
    const available = entry.status === 'available';
    let note: string | null = null;
    let tone: PlaneTone = available ? 'meta' : 'warn';
    if (!available) {
      // Dark plane: visible + interactive, default OFF, amber reason (D8). The
      // consequence clause is appended AFTER the server-provided reason too (not
      // only in the no-reason fallback), so selecting a dark plane always states
      // what will happen.
      note = `${entry.reason || 'unavailable'} — will be reported as unavailable if selected`;
    } else if (key === 'aiq') {
      // Stale-but-present AIQ stays selectable with an amber age note (D8).
      const ageNote = aiqCacheNote(entry, brandSlug);
      if (ageNote) {
        note = ageNote.text;
        tone = ageNote.stale ? 'warn' : 'meta';
      }
    }
    return { key, label: TOGGLE_LABEL[key] || key, available, defaultOn: available, note, tone };
  });
}

/** The `include` array for POST /research from the checked plane keys.
 * Returns undefined when ALL four planes are selected — so a full-coverage run
 * carries no `include` key and matches the pre-H2 (opt-out default) wire body
 * byte-for-byte. Any narrowing sends the explicit array. */
export function includeFromSelection(selected: Iterable<string>): string[] | undefined {
  const set = new Set(selected);
  const chosen = PLANES.filter((p) => set.has(p));
  return chosen.length === PLANES.length ? undefined : chosen;
}

/** The run-modal lede — conditional on which cross-reference planes are
 * selected, so it never promises a join that won't happen. */
export function runLede(selected: Iterable<string>): string {
  const set = new Set(selected);
  const impact = set.has('impact');
  const aiq = set.has('aiq');
  let cross: string;
  if (impact && aiq) cross = ', then cross-references live Google (IMPACT) and AI-narrative (AIQ) data';
  else if (impact) cross = ', then cross-references live Google (IMPACT) data';
  else if (aiq) cross = ', then cross-references AI-narrative (AIQ) data';
  else cross = '';
  return (
    `Maps a client's full digital presence${cross}. ` +
    'Takes ~10–30 minutes and costs a few dollars in API fees — the service runs one report at a time.'
  );
}
